import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { CreateTradingPartnerModal } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/CreateTradingPartnerModal'
import { DeleteTradingPartnerDialog } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/DeleteTradingPartnerDialog'
import { EditTradingPartnerModal } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/EditTradingPartnerModal'
import { TradingPartnerTable } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/TradingPartnerTable'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'
import { convertTradingPartnerListItem } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/convert'
import { tradingPartnerCsvColumns } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/tradingPartnerCsvColumns'
import type { TradingPartnerRow } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/types'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

const DEFAULT_AREA: AreaCode = AreaCode.UNSPECIFIED

function filterRows(
  rows: TradingPartnerRow[],
  areaCode: AreaCode,
  searchText: string,
): TradingPartnerRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows
    .filter((row) => {
      // accounts[].areaCode の any-match (spec AC-002): 1 社が複数エリアに
      // アカウントを持ちうるため、 いずれか 1 件でも一致すれば表示対象とする。
      if (
        areaCode !== AreaCode.UNSPECIFIED &&
        !row.accounts.some((a) => a.areaCode === areaCode)
      ) {
        return false
      }
      if (trimmed === '') {
        return true
      }
      return (
        row.name.toLowerCase().includes(trimmed) ||
        row.shortName.toLowerCase().includes(trimmed)
      )
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * 取引先タブ。
 * 全取引先一覧 (license フィルタなし) + CRUD モーダルを表示する。
 * 旧実装では License 別タブ (発電販売/需要調達/需要抑制) として分割されていたが、
 * 計画マスタ分離に伴い取引先は単一タブに統合した。
 */
export const TradingPartnerTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListTradingPartners()

  const [selectedAreaCode, setSelectedAreaCode] =
    useState<AreaCode>(DEFAULT_AREA)
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () =>
      data !== undefined ? data.items.map(convertTradingPartnerListItem) : [],
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  // 取引先タブは失効フィルタを持たない (expiry オプション未指定)。
  const tab = useMasterCrudTab<TradingPartnerRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix: 'page:adminOcctoMaster.tradingPartner.serverError',
    filteredRows,
    csv: { columns: tradingPartnerCsvColumns(), fileBase: 'trading-partner' },
  })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        tab.modalState.kind === 'create' ? (
          <CreateTradingPartnerModal
            isOpen={tab.modalState.kind === 'create'}
            onClose={tab.closeModal}
          />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteTradingPartnerDialog
            row={deleteRow}
            errorMessage={tab.deleteError?.message}
            isOpen={tab.modalState.kind === 'delete'}
            onClose={tab.closeModal}
            onError={(message) => {
              tab.setDeleteError({ message, retriable: true })
            }}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditTradingPartnerModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.tradingPartner.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <TradingPartnerTable
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
        />
      }
    />
  )
}
